/**
 * Subscription predicates for one entry of `user.organizations` from /api/users/me/
 * (`subscription_status` is built by users/api/serializers.py get_subscription_status).
 *
 * Pure module: store.js imports it, so never import the store from here. The staff bypass
 * lives in SubscriptionGate, not in these helpers.
 */

/** True when the org can be worked in: an active paid subscription or a running trial. */
export function hasValidSubscription(org) {
  const status = org?.subscription_status;
  return Boolean(status?.is_active || status?.is_trial_active);
}

/**
 * 'active' | 'trial' | 'expired' | 'none'.
 * Trial is checked before active: a running trial also has an ACTIVE status row on the backend.
 * `is_trial_active` is absent (undefined) when the org has no subscription at all.
 */
export function getSubscriptionState(org) {
  const status = org?.subscription_status;
  if (!status?.has_subscription) return 'none';
  if (status.is_trial_active) return 'trial';
  if (status.is_active) return 'active';
  return 'expired';
}

/**
 * Add whole months to an ISO date (YYYY-MM-DD), clamping to the month end exactly like
 * Python's dateutil.relativedelta does on the backend: 2027-01-31 + 1 month is
 * 2027-02-28, not March 3rd. Returns an ISO date string, or null for bad input.
 */
export function addMonthsClamped(isoDate, months) {
  if (!isoDate || !Number.isInteger(months)) return null;
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return null;
  const total = y * 12 + (m - 1) + months;
  const year = Math.floor(total / 12);
  const month = total - year * 12; // 0-based
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const day = Math.min(d, lastDay);
  const pad = (n) => String(n).padStart(2, '0');
  return `${year}-${pad(month + 1)}-${pad(day)}`;
}

/** The day before an ISO date, as ISO. discount_end is exclusive; people read the last day. */
export function dayBefore(isoDate) {
  if (!isoDate) return null;
  const [y, m, d] = isoDate.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}

const roundCents = (value) => Math.round(value * 100) / 100;

/**
 * Preview of a percentage discount on a plan fee, mirroring
 * payments/services/subscription_pricing.py (the backend value is authoritative):
 *
 *   discountSummary({ basePrice: 199, percent: 20, months: 24,
 *                     firstFeeDate: '2026-11-01', billingPeriod: 'monthly' })
 *   -> { basePrice: 199, discountAmount: 39.8, price: 159.2,
 *        discountEnd: '2028-11-01', lastDay: '2028-10-31', periods: 24, forever: false }
 *
 * `months` null/undefined = forever. `periods` is how many plan fees are discounted
 * (yearly: months / 12). Returns null when there is no usable percent.
 */
export function discountSummary({ basePrice, percent, months, firstFeeDate, billingPeriod }) {
  const base = Number(basePrice);
  const pct = Number(percent);
  if (!Number.isFinite(base) || !Number.isFinite(pct) || pct <= 0 || pct > 100) return null;
  const discountAmount = roundCents((base * pct) / 100);
  const price = roundCents(base - discountAmount);
  const forever = months === null || months === undefined || months === '';
  const monthCount = forever ? null : Number(months);
  const discountEnd = forever || !firstFeeDate ? null : addMonthsClamped(firstFeeDate, monthCount);
  let periods = null;
  if (!forever && Number.isInteger(monthCount) && monthCount > 0) {
    periods = billingPeriod === 'yearly' ? monthCount / 12 : monthCount;
  }
  return {
    basePrice: base,
    discountAmount,
    price,
    discountEnd,
    lastDay: dayBefore(discountEnd),
    periods,
    forever,
  };
}

/** Yearly billing bills one plan fee per 12 months, so the duration has to be whole years. */
export function isValidDiscountMonths(months, billingPeriod) {
  if (months === null || months === undefined || months === '') return true;
  const n = Number(months);
  if (!Number.isInteger(n) || n <= 0) return false;
  return billingPeriod !== 'yearly' || n % 12 === 0;
}
